import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { SITE_SERVICE_WORKER_RELATIVE_URL } from '@/services/service-worker/config';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface EmarsysConfig {
  domainCode: string;
  defaultNotificationTitle: string;
  defaultNotificationIcon: string;
  applicationServerPublicKey: string;
  scarabJsApiUrl: string;
  isEnabled?: boolean;
}

export class EmarsysProvider implements Provider {
  type = ProviderType.EMARSYS;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: EmarsysConfig;

  constructor(config: EmarsysConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    // 默认在本地环境启用日志
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Initialize Scarab Queue (ScarabQueue array must be initialized regardless of SDK load status)
      // This allows us to push events to the queue even if the SDK script is blocked by CMP.
      // Once the user consents, the SDK will load and process the queued events.
      this.initializeScarabQueue();

      // Initialize Emarsys SDK with timeout handling
      // We use a soft-fail approach: if it times out (likely due to CMP), we still mark the provider
      // as initialized so that we can push events to the queue.
      try {
        const startTime = Date.now();
        await this.initializeEmarsysSDK();
        const duration = Date.now() - startTime;
        logger.info(`Emarsys SDK initialized successfully in ${duration}ms`);
      } catch (error) {
        logger.warn('Emarsys SDK initialization timed out or failed (soft fail):', error);
        // Continue execution even if SDK load fails/times out
      }

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Emarsys Provider:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      // 简洁的日志功能
      if (this.loggingEnabled) {
        logger.info(`Emarsys, Event: ${event.eventName}`, event.properties);
      }

      // 使用ScarabQueue进行事件上报（主要的上报方式）
      if (window.ScarabQueue) {
        const userId = localStorage.getItem('zl-userId');

        // 如果有用户ID且事件名不是setCustomerId，则先设置用户ID
        if (userId && event.eventName !== 'setCustomerId') {
          window.ScarabQueue.push(['setCustomerId', userId]);
        }

        // 推送事件
        window.ScarabQueue.push([event.eventName, event.properties]);

        // 记录推送日志，便于调试
        logger.info(
          `[${new Date().toISOString()}] Emarsys event pushed to queue: ${event.eventName}`,
          event.properties,
        );

        // 执行go命令
        window.ScarabQueue.push(['go']);
      }
    } catch (error) {
      logger.error('Emarsys tracking error:', error);
    }
  }

  /**
   * 启用Emarsys日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Emarsys日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  /**
   * 手动设置用户ID
   * @param userId 用户ID
   */
  setCustomerId(userId: string): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (window.ScarabQueue) {
        window.ScarabQueue.push(['setCustomerId', userId]);
        window.ScarabQueue.push(['go']);
      }
    } catch (error) {
      logger.error('Emarsys setCustomerId error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private async initializeEmarsysSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      // CMP (like ConsentManager) will intercept scripts with data-cmp-ab="1", changing type to text/plain.
      // This prevents onload/onerror from firing, causing the Promise to hang indefinitely.
      // This hangs ReportingCenter's Promise.allSettled, breaking ALL analytics.
      //
      // Solution: 8s timeout with soft-fail logic.
      // If timeout occurs, we reject this specific promise, but the outer init() catches it
      // and proceeds to initialize the queue anyway.
      const TIMEOUT_MS = 8000;
      let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        timer = null;
        reject(new Error('Emarsys SDK load timeout (CMP block likely)'));
      }, TIMEOUT_MS);

      try {
        // Load Emarsys SDK script
        const script = document.createElement('script');
        script.src =
          'https://client-version.cf.emarsys.net/web-emarsys-sdk-v4/latest/web-emarsys-sdk.js';
        script.async = true;
        // Compatible with consentmanager
        script.setAttribute('data-cmp-ab', '1');

        script.onload = () => {
          const isLateLoad = !timer;
          if (timer) {
            clearTimeout(timer);
            timer = null;
          }

          try {
            // Initialize WebEmarsysSdk
            window.WebEmarsysSdk = window.WebEmarsysSdk || ([] as any);

            // Initialize with configuration
            window.WebEmarsysSdk.push([
              'init',
              {
                enableLogging: true,
                applicationCode: this.config.domainCode,
                enableMacSafariVapid: true,
                defaultNotificationTitle: this.config.defaultNotificationTitle,
                defaultNotificationIcon: this.config.defaultNotificationIcon,
                autoSubscribe: false,
                serviceWorker: {
                  // Emarsys 会自行补根斜杠；与 AR 共用版本化入口，避免重新注册旧缓存地址。
                  url: SITE_SERVICE_WORKER_RELATIVE_URL,
                  applicationServerPublicKey: this.config.applicationServerPublicKey,
                },
              },
            ]);

            if (isLateLoad) {
              logger.info('Emarsys SDK loaded late after timeout, initialized successfully');
            }

            resolve();
          } catch (error) {
            if (!isLateLoad) {
              reject(error);
            } else {
              logger.error('Emarsys SDK late load initialization error:', error);
            }
          }
        };

        script.onerror = () => {
          if (timer) {
            clearTimeout(timer);
            timer = null;
          }
          reject(new Error('Failed to load Emarsys SDK script'));
        };

        // Insert script into DOM
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode?.insertBefore(script, firstScript);
      } catch (error) {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        reject(error);
      }
    });
  }

  private initializeScarabQueue(): void {
    try {
      // Initialize ScarabQueue
      window.ScarabQueue = window.ScarabQueue || ([] as any);

      // Load Scarab script
      const scarabScriptId = 'scarab-js-api';
      if (document.getElementById(scarabScriptId)) return;

      const script = document.createElement('script');
      script.id = scarabScriptId;
      script.src = this.config.scarabJsApiUrl;
      script.async = true;
      // 兼容consentmanager的
      script.setAttribute('data-cmp-ab', '1');

      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    } catch (error) {
      logger.error('Failed to initialize Scarab Queue:', error);
    }
  }
}
